// src/pages/game/Lobby.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import styled, { css, keyframes } from "styled-components";
import { Link, useParams, useHistory } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";
import {
  Input,
  ButtonFilled,
  ButtonOutline,
  Header,
  Body,
  Dropdown,
} from "../../styleguide/atoms/index.js";
import { db, ensureAnonAuth } from "../../firebase";
import { api } from "../../lib/gameApi";
import { getCharactersForLobby } from "../../lib/getCharactersForLobby";
import { renderRichText } from "../../lib/renderRichText";
import { CharacterCard } from "./components/CharacterCard";
import Footer from "../Footer.js";
import GameLogo from "../../media/images/summer-camp-logo.png";
import * as styles from "../../styleguide/styles/Stylesheet.js";
import FluidBackground from "./FluidBackground";
import { CloseX } from "./styles/Game.styles";
import Rules from "./components/Rules.js";

const PageContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
`;
const IntroWrapper = styled.div`
  width: 100%;
  max-width: ${styles.width_nrw};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${styles.spacing_standard};
  padding: 88px ${styles.spacing_standard};
  @media only screen and (max-width: ${styles.width_sm}) {
    padding: 88px ${styles.spacing_standard} ${styles.spacing_standard_large};
  }
`;
const LogoContainer = styled.img`
  width: 100%;
  max-width: 512px;
  margin: 0 0 ${styles.spacing_standard_large};
  @media only screen and (max-width: ${styles.width_sm}) {
    margin: 0;
  }
`;
const ShareButtons = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: ${styles.spacing_standard_half};
  padding: ${styles.spacing_standard_half} 0 0;
`;
const ShareButton = styled.div`
  width: 100%;
  max-width: 140px;
  display: flex;
  flex-direction: column;
`;
const SignUpContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 0 ${styles.spacing_standard} ${styles.spacing_standard_large};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  @media only screen and (max-width: ${styles.width_sm}) {
    padding: 0 ${styles.spacing_standard} ${styles.spacing_standard};
  }
`;
const SignUpWrapper = styled.div`
  position: relative;
  padding: 18px ${styles.spacing_standard} 42px;
  width: 100%;
  max-width: 800px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: ${styles.color_white};
  box-shadow: ${styles.box_shadow};
`;
const PlayerListWrapper = styled.div`
  position: relative;
  padding: 42px ${styles.spacing_standard} 18px;
  width: 100%;
  max-width: 800px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: ${styles.color_white};
  box-shadow: ${styles.box_shadow};
  @media only screen and (max-width: ${styles.width_sm}) {
    padding: ${styles.spacing_standard_large} ${styles.spacing_standard};
  }
`;
const CharactersContainer = styled.div`
  position: relative;
  padding: 60px ${styles.spacing_standard} ${styles.spacing_standard};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border-top: 1px solid ${styles.color_gry_2};
  background-color: ${styles.color_white};
  @media only screen and (max-width: ${styles.width_sm}) {
    padding: ${styles.spacing_standard_large} ${styles.spacing_standard};
  }
`;
const SelectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const InfoButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 54px;
  padding: 6px 8px;
  margin-left: 12px;
  border: 2px solid ${styles.color_accent};
  background-color: ${styles.color_white};
  border-radius: 5px;
  cursor: pointer;
  font-size: 12px;
  color: ${styles.color_accent};
  font-family: ${styles.font_sans};
  font-weight: ${styles.font_bold};
  text-transform: uppercase;
  transition: 250ms all;

  &:hover {
    background-color: ${styles.color_accent};
    color: ${styles.color_white};
  }
`;
const InfoCopy = styled.div`
  width: 100%;
  max-width: ${styles.width_sm};
  padding: ${styles.spacing_standard} 0 0;
`;
const InputButtonContainer = styled.div`
  width: 100%;
  max-width: ${styles.width_md};
  padding: ${styles.spacing_standard} 0;
  display: flex;
  flex-direction: column;
`;
const SignUpButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  @media only screen and (max-width: ${styles.width_sm}) {
    flex-direction: column;
  }
`;
const CharactersButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  @media only screen and (max-width: ${styles.width_md}) {
    flex-direction: column;
  }
`;
const WarningCopy = styled.div`
  margin: 8px 0 0 0;
  font-family: ${styles.font_sans};
  font-size: 14px;
  line-height: 1;
  color: ${styles.color_red};
`;
const PlayersList = styled.div`
  width: 100%;
  max-width: ${styles.width_md};
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: center;
  padding: ${styles.spacing_standard} 0;
  gap: ${styles.spacing_standard_half};
`;
const PlayerCard = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  box-shadow: ${styles.box_shadow};
  padding: ${styles.spacing_standard_half} ${styles.spacing_standard};
  font-family: ${styles.font_sans};
  font-size: 18px;
  text-align: center;
  color: ${styles.color_gry_6};
  line-height: 1.4;
`;
const PlayerMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1;
`;
const KickButton = styled.button`
  display: inline-block;
  width: 50px;
  margin-left: 8px;
  padding: 6px 8px;
  border-radius: 5px;
  border: 2px solid ${styles.color_red};
  font-family: ${styles.font_sans};
  font-weight: ${styles.font_bold};
  background: ${styles.color_white};
  color: ${styles.color_red};
  font-size: 12px;
  text-transform: uppercase;
  line-height: 1;
  cursor: pointer;
  transition: 250ms all;

  &:hover {
    background: ${styles.color_red};
    color: ${styles.color_white};
  }
  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.25;
    `}
`;
const ButtonWrapper = styled.div`
  margin: 0 0 0 ${styles.spacing_standard_half};
  width: 100%;
  max-width: 132px;
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: ${styles.width_sm}) {
    margin: ${styles.spacing_standard_half} 0 0 0;
    max-width: unset;
  }
`;
const CharactersButton = styled.div`
  margin: 0 0 0 ${styles.spacing_standard_half};
  width: 100%;
  max-width: 152px;
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: ${styles.width_md}) {
    margin: ${styles.spacing_standard_half} 0 0 0;
    max-width: unset;
  }
`;
const CharactersList = styled.div`
  width: 100%;
  max-width: 1600px;
  padding: ${styles.spacing_standard_large} 0 0;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: ${styles.spacing_standard};
  ${({ listView }) =>
    listView &&
    css`
      flex-direction: column;
      align-items: center;
    `}
`;
const StartGameWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  max-width: ${styles.width_md};
`;
const WarningsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${styles.spacing_standard_half};
  padding-top: ${styles.spacing_standard};
`;
const PageOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${styles.spacing_standard};
`;
export const LoadingSpinnerContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
 `;
export const LoadingSpinner = styled.div`
  width: 42px;
  height: 42px;
  border: 6px solid ${styles.color_white};
  border-top-color: ${styles.color_accent_dark};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;
const PlayerTag = styled.span`
  display: inline-block;
  width: 50px;
  margin-left: 8px;
  padding: 6px 8px;
  border-radius: 5px;
  border: 2px solid ${styles.color_gry_1};
  font-family: ${styles.font_sans};
  font-weight: ${styles.font_bold};
  background: ${styles.color_gry_1};
  color: ${styles.color_accent_dark};
  font-size: 12px;
  text-transform: uppercase;
  line-height: 1;
  ${({ host }) =>
    host &&
    css`
      border: 2px solid ${styles.color_accent};
      background: ${styles.color_accent};
      color: ${styles.color_white};
    `}
`;
const HomeButton = styled(Link)`
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  border: 2px solid ${styles.color_white};
  border-radius: 5px;
  cursor: pointer;
  font-size: 12px;
  color: ${styles.color_white};
  font-family: ${styles.font_sans};
  font-weight: ${styles.font_bold};
  text-decoration: none;
  text-transform: uppercase;
  transition: 250ms all;
  z-index: 1;

  &:hover {
    background-color: ${styles.color_white};
    color: ${styles.color_accent};
  }
`;
const HowToPlayButton = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  border: 2px solid ${styles.color_white};
  border-radius: 5px;
  cursor: pointer;
  font-size: 12px;
  color: ${styles.color_white};
  font-family: ${styles.font_sans};
  font-weight: ${styles.font_bold};
  text-decoration: none;
  text-transform: uppercase;
  transition: 250ms all;
  z-index: 1;

  &:hover {
    background-color: ${styles.color_white};
    color: ${styles.color_accent};
  }
`;
const SettingsButton = styled.div`
  position: absolute;
  top: 12px;
  right: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  border: 2px solid ${styles.color_white};
  border-radius: 5px;
  cursor: pointer;
  font-size: 12px;
  color: ${styles.color_white};
  font-family: ${styles.font_sans};
  font-weight: ${styles.font_bold};
  text-decoration: none;
  text-transform: uppercase;
  transition: 250ms all;
  z-index: 1;

  &:hover {
    background-color: ${styles.color_white};
    color: ${styles.color_accent};
  }
`;
const SettingsContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: ${styles.color_white};
  border-radius: 10px;
  padding: ${styles.spacing_standard_large};
  @media only screen and (max-width: ${styles.width_sm}) {
    max-height: 80vh;
    padding: ${styles.spacing_standard};
  }
`;
const SettingsFields = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${styles.spacing_standard};
  padding: ${styles.spacing_standard} 0 0;
`;
const ErrorMessageCard = styled.div`
  max-width: 420px;
  padding: 24px 32px;
  border-radius: 10px;
  background: ${styles.color_white};
  color: ${styles.color_gry_6};
  box-shadow: ${styles.box_shadow};
  text-align: center;
  font-family: ${styles.font_sans};
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const ErrorWrapper = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${styles.spacing_standard};
`;
const RulesContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: ${styles.width_nrw};
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${styles.color_white};
  border-radius: 10px;
  @media only screen and (max-width: ${styles.width_sm}) {
    max-height: 80vh;
  }
`;

export default function Lobby() {
  const { gameId } = useParams();
  const history = useHistory();
  const [game, setGame] = useState(null);
  const [players, setPlayers] = useState([]);
  const [joinedReady, setJoinedReady] = useState(false);
  const [name, setName] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [roles, setRoles] = useState([]);
  const [listView, setListView] = useState(false);
  const [search, setSearch] = useState("");
  const [signingUp, setSigningUp] = useState(false);
  const [starting, setStarting] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [isKicking, setIsKicking] = useState(false);
  const [playerInfoOpen, setPlayerInfoOpen] = useState(false);
  const [roleInfoOpen, setRoleInfoOpen] = useState(false);
  const [howToPlayOpen, setHowToPlayOpen] = useState(false);
  const [uid, setUid] = React.useState(null);
  const [joinError, setJoinError] = useState("");
  const buttonText = listView ? "Card View" : "List View";

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [nightSeconds, setNightSeconds] = useState(60);
  const [daySeconds, setDaySeconds] = useState(60);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState("");

  const phaseDurationOptions = [
    { text: "30 seconds", value: 30 },
    { text: "45 seconds", value: 45 },
    { text: "60 seconds", value: 60 },
    { text: "75 seconds", value: 75 },
    { text: "90 seconds", value: 90 },
  ];

  useEffect(() => {
    if (!game || !game.options) return;
    const { nightSeconds: n, daySeconds: d } = game.options;
    if (typeof n === "number" && isFinite(n)) {
      setNightSeconds(n);
    }
    if (typeof d === "number" && isFinite(d)) {
      setDaySeconds(d);
    }
  }, [game]);

  async function handleNightDurationSelect(text, value) {
    if (!isHost) return;
    const v = Number(value);
    if (!v || v === nightSeconds) {
      setNightSeconds(v);
      return;
    }
    setNightSeconds(v);
    setSettingsSaving(true);
    setSettingsError("");
    try {
      await api.setPhaseDurations(gameId, v, undefined);
    } catch (e) {
      console.error("Failed to update night duration", e);
      setSettingsError(e?.message || "Failed to save settings.");
    } finally {
      setSettingsSaving(false);
    }
  }

  async function handleDayDurationSelect(text, value) {
    if (!isHost) return;
    const v = Number(value);
    if (!v || v === daySeconds) {
      setDaySeconds(v);
      return;
    }
    setDaySeconds(v);
    setSettingsSaving(true);
    setSettingsError("");
    try {
      await api.setPhaseDurations(gameId, undefined, v);
    } catch (e) {
      console.error("Failed to update day duration", e);
      setSettingsError(e?.message || "Failed to save settings.");
    } finally {
      setSettingsSaving(false);
    }
  }

  const nightItems = phaseDurationOptions.map((opt) => ({
    ...opt,
    selected: opt.value === nightSeconds,
  }));

  const dayItems = phaseDurationOptions.map((opt) => ({
    ...opt,
    selected: opt.value === daySeconds,
  }));

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    let stopPlayerWatch = null;
    (async () => {
      try {
        const meRef = doc(db, "games", gameId, "players", uid);
        const meSnap = await getDoc(meRef);
        if (meSnap.exists()) {
          await setDoc(meRef, { inLobby: true }, { merge: true });
          if (!cancelled) setJoinedReady(true);
        } else {
          await api.joinGameById(gameId);
          if (cancelled) return;
          stopPlayerWatch = onSnapshot(
            meRef,
            (snap) => {
              if (snap.exists()) {
                setJoinedReady(true);
              }
            },
            (err) => {
              console.warn("player doc watch error:", err?.message || err);
            }
          );
        }
      } catch (e) {
        console.error("Auto-join failed:", e);
        if (!cancelled) {
          setJoinError(
            e?.message ||
              "This room no longer exists or has expired. Ask your host to share a new link."
          );
        }
      }
    })();
    return () => {
      cancelled = true;
      if (typeof stopPlayerWatch === "function") stopPlayerWatch();
      setJoinedReady(false);
    };
  }, [gameId, uid]);

  useEffect(() => {
    const checkGame = async () => {
      try {
        const ref = doc(db, "games", gameId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setJoinError(
            "This room no longer exists or has expired. Ask your host to create a new game."
          );
        }
      } catch (e) {
        console.warn("Failed to check game existence:", e);
      }
    };
    checkGame();
  }, [gameId]);

  // Ensure auth, then expose uid for dependent effects
  useEffect(() => {
    let alive = true;
    (async () => {
      const cred = await ensureAnonAuth();
      if (!alive) return;
      setUid(api.me()?.uid || cred?.user?.uid || null);
    })();
    return () => {
      alive = false;
    };
  }, []);

  // who am I?
  const me = useMemo(
    () => players.find((p) => p.id === api.me()?.uid),
    [players]
  );
  const hasName = !!me?.name;

  const hostId = useMemo(() => {
    if (game?.hostId) return game.hostId;
    if (game?.ownerId) return game.ownerId;
    if (game?.createdBy) return game.createdBy;

    const flagged = players.find((p) => p.isHost === true);
    if (flagged) return flagged.id;

    return players[0]?.id ?? null;
  }, [game?.hostId, game?.ownerId, game?.createdBy, players]);

  const isHost = me?.id && hostId ? me.id === hostId : false;

  const playersInLobby = useMemo(
    () => players.filter((p) => p.inLobby === true),
    [players]
  );

  const playersForList = useMemo(() => {
    if (game?.status === "ended") {
      const namedPlayers = players.filter(
        (p) => !!p.name && (p.inLobby === true || p.isSpectator !== true)
      );

      const unnamedSpectatorsInLobby = players.filter(
        (p) => !p.name && p.inLobby === true
      );

      return [...namedPlayers, ...unnamedSpectatorsInLobby];
    }

    return playersInLobby;
  }, [players, playersInLobby, game?.status]);

  const eligiblePlayers = useMemo(
    () =>
      players.filter(
        (p) =>
          !!p.name &&
          (p.inLobby === true ||
            (game?.status === "ended" && p.isSpectator !== true))
      ),
    [players, game?.status]
  );

  const playerCount = eligiblePlayers.length;

  const roleMap = useMemo(
    () =>
      Object.fromEntries(
        roles.map((r) => [
          r.id,
          {
            title: r.title,
            team: r.team,
            description: r.description,
            image: r.imageUrl,
          },
        ])
      ),
    [roles]
  );

  const villainSelected = useMemo(
    () => selectedIds.some((id) => roleMap[id]?.team === "Villain"),
    [selectedIds, roleMap]
  );

  const villainCountSelected = useMemo(
    () =>
      selectedIds.reduce(
        (n, id) => n + (roleMap[id]?.team === "Villain" ? 1 : 0),
        0
      ),
    [selectedIds, roleMap]
  );
  const nonVillainCountSelected = selectedIds.length - villainCountSelected;
  const exactPick = selectedIds.length === playerCount;
  const overPick = selectedIds.length > playerCount;
  const underPick = selectedIds.length < playerCount;
  const hasEnoughPlayers = playerCount >= (game?.minPlayers || 4);

  // exact pick: keep legacy validation (campers must outnumber villains)
  const exactCanStart =
    hasEnoughPlayers &&
    exactPick &&
    roles.length > 0 &&
    selectedIds.every((id) => !!roleMap[id]) &&
    villainSelected &&
    villainCountSelected < nonVillainCountSelected;

  // over pick: must be feasible to draw an assignment that has >=1 villain,
  // and if playerCount < 7, at most 1 villain ⇒ need enough non-villains.
  const overCanStart =
    hasEnoughPlayers &&
    overPick &&
    roles.length > 0 &&
    selectedIds.every((id) => !!roleMap[id]) &&
    villainSelected &&
    (playerCount >= 7
      ? true // any mix is fine; we’ll enforce ≥1 villain in server sampler
      : nonVillainCountSelected >= Math.max(0, playerCount - 1)); // can we fill remaining with non-villains?

  const canStart = exactCanStart || overCanStart;

  // Subscribe to game + players ONLY after my players/{uid} exists (joinedReady)
  useEffect(() => {
    if (!joinedReady) return;
    const stopGame = onSnapshot(
      doc(db, "games", gameId),
      (d) => {
        const data = d.data();
        setGame({ id: d.id, ...data });
        if (data?.status === "running") history.push(`/game/${gameId}`);
        setSelectedIds(data?.selectedCharacterIds || []);
      },
      (err) => console.warn("game watch error:", err?.message || err)
    );
    const stopPlayers = onSnapshot(
      query(
        collection(db, "games", gameId, "players"),
        orderBy("joinedAt", "asc")
      ),
      (snap) => setPlayers(snap.docs.map((x) => ({ id: x.id, ...x.data() }))),
      (err) => console.warn("players watch error:", err?.message || err)
    );
    return () => {
      stopGame?.();
      stopPlayers?.();
    };
  }, [gameId, history, joinedReady]);

  // contentful once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const playable = await getCharactersForLobby({ playableOnly: true });
      if (!cancelled) setRoles(playable);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Clear any “copied” timeouts on unmount to avoid setState after unmount
  const timeoutsRef = useRef(new Set());

  const addTimeout = (fn, ms) => {
    const id = window.setTimeout(() => {
      try {
        fn();
      } finally {
        timeoutsRef.current.delete(id);
      }
    }, ms);
    timeoutsRef.current.add(id);
    return id;
  };

  useEffect(() => {
    const bucket = timeoutsRef.current; // capture the Set once
    return () => {
      bucket.forEach((id) => clearTimeout(id));
      bucket.clear();
    };
  }, []);

  // Keep my "inLobby" flag accurate (only after my player doc exists)
  useEffect(() => {
    if (!uid || !joinedReady) return;
    const ref = doc(db, "games", gameId, "players", uid);
    let hasDoc = false;
    const stop = onSnapshot(ref, (snap) => {
      hasDoc = snap.exists();
      if (hasDoc) {
        setDoc(ref, { inLobby: true }, { merge: true }).catch((e) =>
          console.warn(e)
        );
      }
    });
    const markOut = async () => {
      if (!hasDoc) return;
      try {
        await setDoc(ref, { inLobby: false }, { merge: true });
      } catch (e) {
        console.warn(e);
      }
    };

    const onHide = () => {
      // attempt to mark out on page hide / unload
      markOut();
    };
    window.addEventListener("pagehide", onHide);
    window.addEventListener("beforeunload", onHide);

    return () => {
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onHide);
      // navigating away within the SPA
      markOut();
      stop();
    };
  }, [gameId, uid, joinedReady]);

  // local duplicate-name check (server also enforces)
  const nameTaken = useMemo(() => {
    const n = name.trim().toLowerCase();
    if (!n) return false;
    return players.some(
      (p) => p.id !== me?.id && (p.name || "").toLowerCase() === n
    );
  }, [name, players, me?.id]);

  async function doSignUp() {
    if (!name.trim() || nameTaken) return;
    setSigningUp(true);
    try {
      await api.signUp(gameId, name.trim());
      setName("");
    } catch (e) {
      alert(e?.message || "Failed to save name.");
    } finally {
      setSigningUp(false);
    }
  }

  async function copyInviteLink() {
    const url = `${window.location.origin}/lobby/${game?.id || gameId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      addTimeout(() => setCopiedLink(false), 1500);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopiedLink(true);
        addTimeout(() => setCopiedLink(false), 1500);
      } finally {
        document.body.removeChild(ta);
      }
    }
  }

  async function copyCode() {
    const code = game?.code || "";
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(true);
      addTimeout(() => setCopiedCode(false), 1500);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = code;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopiedCode(true);
        addTimeout(() => setCopiedCode(false), 1500);
      } finally {
        document.body.removeChild(ta);
      }
    }
  }

  function toggle(id) {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    setSelectedIds(next);
    api.updateSelection(gameId, next);
  }

  const [serverStartError, setServerStartError] = useState("");

  async function doStart() {
    if (!canStart) return;
    setStarting(true);
    setServerStartError("");
    try {
      // send the full selection, including over-picks
      await api.startGame(gameId, roleMap);
    } catch (e) {
      setServerStartError(e?.message || "Failed to start the game.");
    } finally {
      setStarting(false);
    }
  }

  async function handleKick(playerId, playerName) {
    if (!isHost) return;

    const ok = window.confirm(
      playerName
        ? `Turn ${playerName} into a spectator?`
        : "Turn this player into a spectator?"
    );
    if (!ok) return;
    setIsKicking(true);

    try {
      await api.kickPlayer(gameId, playerId);
      setIsKicking(false);
    } catch (e) {
      console.warn("Failed to remove player:", e);
      alert(e?.message || "Failed to remove player.");
    }
  }

  const selectAllCharacters = () => {
    if (!filteredRoles.length) return;

    // Are all visible roles already selected?
    const filteredSet = new Set(filteredRoles.map((r) => r.id));
    const allFilteredSelected = filteredRoles.every((r) =>
      selectedIds.includes(r.id)
    );

    let next;
    if (allFilteredSelected) {
      // Unselect all visible roles
      next = selectedIds.filter((id) => !filteredSet.has(id));
    } else {
      // Select all visible roles
      next = Array.from(
        new Set([...selectedIds, ...filteredRoles.map((r) => r.id)])
      );
    }

    setSelectedIds(next);
    api.updateSelection(gameId, next);
  };

  // filter roles by search (title or team)
  const filteredRoles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter((r) => {
      const title = (r.title || "").toLowerCase();
      const team = (r.team || "").toLowerCase();
      return title.includes(q) || team.includes(q);
    });
  }, [roles, search]);

  const allFilteredSelected = useMemo(() => {
    if (!filteredRoles.length) return false;
    return filteredRoles.every((r) => selectedIds.includes(r.id));
  }, [filteredRoles, selectedIds]);

  const selectAllButtonText = allFilteredSelected
    ? "Unselect All"
    : "Select All";

  // Tracks the previous player count to detect increases only
  const prevPlayerCountRef = useRef(playerCount);

  useEffect(() => {
    if (game?.status !== "lobby") return;
    if (!roles.length) return;

    const prev = prevPlayerCountRef.current ?? 0;
    const curr = playerCount;

    // Only react to increases in player count
    if (curr > prev) {
      // Only fill if we have fewer selected than players (never add if user already over-selected)
      const need = curr - (selectedIds?.length ?? 0);
      if (need > 0) {
        api.reseedSelection(gameId, roleMap).catch(() => {});
      }
    }

    prevPlayerCountRef.current = curr;
  }, [game?.status, roles, playerCount, selectedIds?.length, roleMap, gameId]);

  const warnings =
    !hasEnoughPlayers ||
    !villainSelected ||
    (underPick && !exactPick) ||
    (exactPick && !(villainCountSelected < nonVillainCountSelected)) ||
    (overPick &&
      playerCount < 7 &&
      nonVillainCountSelected < Math.max(0, playerCount - 1));

  const loading = !uid || (!joinError && (!joinedReady || !game));

  if (joinError) {
    return (
      <PageContainer>
        <FluidBackground normalHeight={true}>
          <HomeButton to="/">Home</HomeButton>
          <ErrorWrapper>
            <ErrorMessageCard>
              <Header type="h2" align="center" padding="0">
                Room not available
              </Header>
              <Body align="center">
                {joinError ||
                  "This room no longer exists or has expired. Ask your host to create a new game."}
              </Body>
              <ButtonFilled
                text="Back to Home"
                onClick={() => history.push("/")}
              />
            </ErrorMessageCard>
          </ErrorWrapper>
        </FluidBackground>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HomeButton to="/">Home</HomeButton>
      <HowToPlayButton onClick={() => setHowToPlayOpen((v) => !v)}>
        How to play
      </HowToPlayButton>
      {howToPlayOpen ? (
        <PageOverlay onClick={() => setHowToPlayOpen(false)}>
          <RulesContainer>
            <CloseX onClick={() => setHowToPlayOpen(false)} aria-label="Close">
              ×
            </CloseX>
            <Rules></Rules>
          </RulesContainer>
        </PageOverlay>
      ) : null}
      {isHost ? (
        <SettingsButton onClick={() => setSettingsOpen((v) => !v)}>
          Settings
        </SettingsButton>
      ) : null}
      {settingsOpen ? (
        <PageOverlay>
          <SettingsContainer>
            <CloseX
              onClick={() => setSettingsOpen(false)}
              aria-label="Close settings"
            >
              ×
            </CloseX>
            <Header type="h2" align="center">
              Game Settings
            </Header>

            <SettingsFields>
              <Dropdown
                key={`night-${nightSeconds}`}
                label="Night Time"
                text={`${nightSeconds || 60} seconds`}
                items={nightItems}
                onSelect={handleNightDurationSelect}
                disabled={settingsSaving}
              />
              <Dropdown
                key={`day-${daySeconds}`}
                label="Day Time"
                text={`${daySeconds || 60} seconds`}
                items={dayItems}
                onSelect={handleDayDurationSelect}
                disabled={settingsSaving}
              />
              {settingsSaving && (
                <Body align="center" color={styles.color_gry_4}>
                  Saving…
                </Body>
              )}
              {settingsError && (
                <Body align="center" color={styles.color_red}>
                  <b>{settingsError}</b>
                </Body>
              )}
            </SettingsFields>
          </SettingsContainer>
        </PageOverlay>
      ) : null}

      {loading ? (
        <PageOverlay>
          <LoadingSpinnerContainer>
            <LoadingSpinner />
          </LoadingSpinnerContainer>
        </PageOverlay>
      ) : null}
      <FluidBackground normalHeight={true}>
        <IntroWrapper>
          <LogoContainer src={GameLogo} />
          <Header
            type="h1"
            align="center"
            color={styles.color_white}
            padding="0"
          >
            Game Code: {game?.code}
          </Header>
          <Body align="center" color={styles.color_white}>
            Share this code, or the link, to invite other players to join.
          </Body>
          <ShareButtons>
            <ShareButton>
              <ButtonOutline
                onClick={copyCode}
                text={copiedCode ? "Copied!" : "Copy Code"}
                reversed
              />
            </ShareButton>
            <ShareButton>
              <ButtonOutline
                onClick={copyInviteLink}
                text={copiedLink ? "Copied!" : "Copy Link"}
                reversed
              />
            </ShareButton>
          </ShareButtons>
        </IntroWrapper>
      </FluidBackground>

      {/* Sign Up */}
      <SignUpContainer>
        <SignUpWrapper>
          <InputButtonContainer>
            <Header type="h3" align="center">
              {hasName ? `Your name: ${me?.name}` : "Enter a name to join."}
            </Header>
            <SignUpButtonWrapper>
              <Input
                id="sign-up"
                placeholder={hasName ? "Change Name" : "Enter name"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={nameTaken}
                disabled={signingUp}
                maxLength={32}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (name.trim()) doSignUp();
                  }
                }}
              />
              <ButtonWrapper margin={nameTaken}>
                <ButtonFilled
                  text={
                    signingUp ? "Submitting" : hasName ? "Change" : "Sign Up"
                  }
                  disabled={signingUp || !name.trim() || nameTaken}
                  onClick={doSignUp}
                />
              </ButtonWrapper>
            </SignUpButtonWrapper>
            {nameTaken ? (
              <WarningCopy>That name is already taken.</WarningCopy>
            ) : null}
          </InputButtonContainer>

          {isHost ? (
            <StartGameWrapper>
              <ButtonFilled
                disabled={!canStart || starting}
                text={starting ? "Starting" : "Start Game"}
                onClick={doStart}
              />
              {warnings ? (
                <WarningsWrapper>
                  {serverStartError && (
                    <Body color={styles.color_red} align="center">
                      <b>{serverStartError}</b>
                    </Body>
                  )}
                  {!hasEnoughPlayers ? (
                    <Body color={styles.color_red} align="center">
                      <b>At least 4 players must join to start a game.</b>
                    </Body>
                  ) : (
                    <>
                      {!villainSelected && (
                        <Body color={styles.color_red} align="center">
                          <b>Select at least one villain.</b>
                        </Body>
                      )}
                      {exactPick &&
                        !(villainCountSelected < nonVillainCountSelected) && (
                          <Body color={styles.color_red} align="center">
                            <b>
                              You must have more non-villain players than
                              villains.
                            </b>
                          </Body>
                        )}
                      {underPick && (
                        <Body color={styles.color_red} align="center">
                          <b>Select at least {playerCount} roles.</b>
                        </Body>
                      )}
                      {overPick &&
                        playerCount < 7 &&
                        nonVillainCountSelected <
                          Math.max(0, playerCount - 1) && (
                          <Body color={styles.color_red} align="center">
                            <b>
                              For fewer than 7 players, you must have at least{" "}
                              {Math.max(0, playerCount - 1)} non-villains
                              available.
                            </b>
                          </Body>
                        )}
                    </>
                  )}
                </WarningsWrapper>
              ) : null}
            </StartGameWrapper>
          ) : (
            <Body align="center">The host must start the game.</Body>
          )}
        </SignUpWrapper>
      </SignUpContainer>

      {/* Player List */}
      <SignUpContainer>
        <PlayerListWrapper>
          <SelectionHeader>
            <Header type="h3" align="center" padding="0">
              {playerCount} Player{playerCount !== 1 ? "s" : null}
            </Header>{" "}
            <InfoButton onClick={() => setPlayerInfoOpen((v) => !v)}>
              {" "}
              {playerInfoOpen ? "close" : "info"}
            </InfoButton>
          </SelectionHeader>
          {playerInfoOpen ? (
            <InfoCopy>
              <Body align="center">
                <p>
                  Players join the game by entering their name.
                  <br />
                  Players that do not sign up will join as spectators.
                  <br />
                  Player wins are tracked in (parentheses).
                </p>
              </Body>
            </InfoCopy>
          ) : null}
          <PlayersList>
            {playersForList.map((p) => {
              const isMe = p.id === me?.id;
              const isHostPlayer = p.id === hostId;
              const wins =
                typeof p.wins === "number" && p.wins > 0 ? ` (${p.wins})` : "";
              const display = p.name ? `${p.name}${wins}` : null;

              const canKick = isHost && !isHostPlayer;

              return (
                <PlayerCard key={p.id}>
                  <PlayerMain>
                    {display ? (
                      <>
                        {isMe ? <b>{display}</b> : display}
                        {isMe && !isHostPlayer && <PlayerTag>You</PlayerTag>}
                        {isHostPlayer && <PlayerTag host>Host</PlayerTag>}
                        {canKick && (
                          <>
                            {isKicking ? (
                              <KickButton disabled>KICK</KickButton>
                            ) : (
                              <KickButton
                                type="button"
                                onClick={() => handleKick(p.id, p.name)}
                                aria-label={`Remove ${p.name || "player"}`}
                              >
                                KICK
                              </KickButton>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        Waiting for player to join...
                        {isMe && !isHostPlayer && <PlayerTag>You</PlayerTag>}
                        {isHostPlayer && <PlayerTag host>Host</PlayerTag>}
                      </>
                    )}
                  </PlayerMain>
                </PlayerCard>
              );
            })}
          </PlayersList>
        </PlayerListWrapper>
      </SignUpContainer>

      {/* Search + view toggle + selection */}
      <CharactersContainer>
        <SelectionHeader>
          <Header type="h3" align="center" padding="0">
            Role Selection ({selectedIds.length})
          </Header>{" "}
          <InfoButton onClick={() => setRoleInfoOpen((v) => !v)}>
            {" "}
            {roleInfoOpen ? "close" : "info"}
          </InfoButton>
        </SelectionHeader>
        {roleInfoOpen ? (
          <InfoCopy>
            <Body align="center">
              <p>
                Roles you select are randomly assigned to players at the
                beginning of the game.
                <br />
                <br />
                If you select more roles than players, the roles not assigned to
                a player are still added to the in-game roster to heighten the
                uncertainty of who’s who.
                <br />
                <br />
                If you select more roles than players, and enough villains for
                following thresholds, the number of villains randomly assigned
                is determined by the player count:
                <br />
                <br />
                4&ndash;6 players: 1 villain
                <br />
                7&ndash;9 players: 2 villains
                <br />
                10&ndash;12 players: 3 villains
                <br />
                13+ players: 4 villains
              </p>
            </Body>
          </InfoCopy>
        ) : null}
        <InputButtonContainer>
          <CharactersButtonWrapper>
            <Input
              id="search-characters"
              placeholder="Search characters"
              value={search}
              onChange={(e) => setSearch(e.target?.value ?? "")}
              search
              maxLength={32}
            />
            <CharactersButton>
              <ButtonFilled
                text={buttonText}
                onClick={() => setListView((v) => !v)}
              />
            </CharactersButton>
            {isHost ? (
              <CharactersButton>
                <ButtonFilled
                  text={selectAllButtonText}
                  onClick={selectAllCharacters}
                />
              </CharactersButton>
            ) : null}
          </CharactersButtonWrapper>
        </InputButtonContainer>

        {isHost ? (
          <StartGameWrapper>
            <ButtonFilled
              disabled={!canStart || starting}
              text={starting ? "Starting" : "Start Game"}
              onClick={doStart}
            />
            {warnings ? (
              <WarningsWrapper>
                {serverStartError && (
                  <Body color={styles.color_red} align="center">
                    <b>{serverStartError}</b>
                  </Body>
                )}
                {!hasEnoughPlayers ? (
                  <Body color={styles.color_red} align="center">
                    <b>At least 4 players must join to start a game.</b>
                  </Body>
                ) : (
                  <>
                    {!villainSelected && (
                      <Body color={styles.color_red} align="center">
                        <b>Select at least one villain.</b>
                      </Body>
                    )}
                    {exactPick &&
                      !(villainCountSelected < nonVillainCountSelected) && (
                        <Body color={styles.color_red} align="center">
                          <b>
                            You must have more non-villain players than
                            villains.
                          </b>
                        </Body>
                      )}
                    {underPick && (
                      <Body color={styles.color_red} align="center">
                        <b>Select at least {playerCount} roles.</b>
                      </Body>
                    )}
                    {overPick &&
                      playerCount < 7 &&
                      nonVillainCountSelected <
                        Math.max(0, playerCount - 1) && (
                        <Body color={styles.color_red} align="center">
                          <b>
                            For fewer than 7 players, you must have at least{" "}
                            {Math.max(0, playerCount - 1)} non-villains
                            available.
                          </b>
                        </Body>
                      )}
                  </>
                )}
              </WarningsWrapper>
            ) : null}
          </StartGameWrapper>
        ) : (
          <Body align="center">Only the host can select characters.</Body>
        )}

        <CharactersList listView={listView}>
          {filteredRoles.map((r) => {
            const isSelected = selectedIds.includes(r.id);
            const descriptionNodes = r.descriptionRich ? (
              renderRichText(r.descriptionRich)
            ) : r.description ? (
              <>{r.description}</>
            ) : null;
            return (
              <CharacterCard
                key={r.id}
                image={r.imageUrl}
                description={descriptionNodes}
                role={r.team}
                title={r.title}
                selected={isSelected}
                onClick={isHost ? () => toggle(r.id) : null}
                listView={listView}
              />
            );
          })}
          <Header type="h3" align="center" padding="52px 0">
            More Roles Coming Soon!
          </Header>
        </CharactersList>
      </CharactersContainer>
      <Footer />
    </PageContainer>
  );
}
